/**
 * WorksPage — bibliothèque d'œuvres avec recherche avancée (Direction C).
 *
 * Header :
 *   - Eyebrow « Bibliothèque · N œuvres » (compteur dérivé du résultat filtré)
 *   - Titre « Œuvres »
 *   - Actions : <SearchInput> inline + bouton « + Nouvelle œuvre »
 *
 * Sous le header, deux rangées de filtres :
 *   - Rangée 1 : <Tabs> statut (Toutes / Brouillons / Publiées / Archivées)
 *                à gauche, sélects « SÉRIE » + « TRI » à droite.
 *   - Rangée 2 : <TagPicker> + sélect « Format » + <FilterChips>.
 *
 * Grille de cartes œuvres : vignette WebP (`thumb_minio_key` -> `MediaImage`),
 * titre, série, 3 tags, statut.
 *
 * État URL via `useListFilters(workListFiltersSchema)` — refresh / partage de
 * lien / back-forward navigateur préservent l'état exact.
 */

import { useMemo, useState } from 'react'
import { ImagePlus, Plus } from 'lucide-react'
import { useNavigate } from 'react-router'

import Button from '@/components/ui/Button'
import EmptyState from '@/components/ui/EmptyState'
import FilterChips, { type FilterChip } from '@/components/ui/FilterChips'
import Pagination from '@/components/ui/Pagination'
import SearchInput from '@/components/ui/SearchInput'
import Select from '@/components/ui/Select'
import TagPicker, { type Tag } from '@/components/ui/TagPicker'
import Tabs from '@/components/ui/Tabs'
import WorkCard from '@/components/works/WorkCard'
import Page from '@/components/layout/Page'
import useListFilters from '@/hooks/useListFilters'
import { useSeriesList, useTags, useWorks } from '@/hooks/useWorks'
import { tagsApi } from '@/utils/api/tags'
import { workListFiltersSchema } from '@/schemas/works'
import {
  WORK_ORIENTATION_FR,
  WORK_ORIENTATION_OPTIONS,
  WORK_SORT_FR,
  WORK_SORT_OPTIONS,
  WORK_STATUS_FR,
  type TagResponse,
  type WorkSortKey,
  type WorkStatus,
} from '@/types/works'

import './WorksPage.css'

type StatusTab = 'all' | 'pending_drafts' | WorkStatus

function tagResponseToTag(t: TagResponse): Tag {
  return {
    id: t.id,
    slug: t.slug,
    label: t.label_fr,
    kind: t.kind,
  }
}

export default function WorksPage() {
  const navigate = useNavigate()

  const {
    filters,
    setFilter: rawSetFilter,
    removeFilter: rawRemoveFilter,
    clear,
    activeKeys,
  } = useListFilters(workListFiltersSchema)

  // S5c — wrapper qui resette `page` à la valeur par défaut quand on
  // change n'importe quel autre filtre. Sinon : utilisateur sur page 5
  // tape une recherche → grille vide (la nouvelle page filtrée n'a pas 5
  // pages). Comportement attendu de tout admin de table paginée.
  const setFilter = <K extends keyof typeof filters>(
    key: K,
    value: (typeof filters)[K] | undefined,
  ) => {
    rawSetFilter(key, value)
    if (key !== 'page' && filters.page !== undefined) {
      rawRemoveFilter('page')
    }
  }
  const removeFilter = <K extends keyof typeof filters>(key: K) => {
    rawRemoveFilter(key)
    if (key !== 'page' && filters.page !== undefined) {
      rawRemoveFilter('page')
    }
  }

  // Buffer local pour le SearchInput — l'URL n'est mise à jour qu'après
  // debounce (`onSearch` de SearchInput, 300ms). Évite un re-fetch et un
  // history.replace sur chaque frappe clavier. Le pattern "adjust state
  // when prop changes" (React 19) resync `searchDraft` quand `filters.q`
  // change depuis l'extérieur (back/forward navigateur, click sur une
  // chip <FilterChips>) sans utiliser useEffect.
  const [searchDraft, setSearchDraft] = useState(filters.q ?? '')
  const [prevFilterQ, setPrevFilterQ] = useState(filters.q ?? '')
  if (prevFilterQ !== (filters.q ?? '')) {
    setPrevFilterQ(filters.q ?? '')
    setSearchDraft(filters.q ?? '')
  }

  const sort: WorkSortKey = filters.sort ?? (filters.q ? 'relevance' : 'recent')

  // S5c — `page` est dans l'URL pour deep-link ; `page_size` reste local
  // (pas de UI selector pour l'instant). Default 50, taille raisonnable
  // pour une grille de cartes œuvres.
  const page = filters.page ?? 1
  const pageSize = 50

  const worksQuery = useWorks({
    q: filters.q,
    tags: filters.tags,
    series_id: filters.series_id,
    status: filters.status,
    orientation: filters.orientation,
    date_from: filters.date_from,
    date_to: filters.date_to,
    has_pending_drafts: filters.has_pending_drafts,
    sort,
    page,
    page_size: pageSize,
  })

  const seriesQuery = useSeriesList()

  // Tags actuellement sélectionnés (pour <TagPicker>) — il faut résoudre les
  // ids vers leurs labels FR via une seconde requête. On charge tous les
  // tags utilisés (cache 60s) et filtre côté client.
  const allTagsQuery = useTags({ limit: 200 })

  const selectedTags: Tag[] = useMemo(() => {
    const ids = filters.tags ?? []
    if (ids.length === 0) return []
    const all = allTagsQuery.data ?? []
    return all.filter((t) => ids.includes(t.id)).map(tagResponseToTag)
  }, [filters.tags, allTagsQuery.data])

  const seriesOptions = useMemo(() => {
    return (seriesQuery.data ?? []).map((s) => ({
      value: String(s.id),
      label: s.name_fr,
    }))
  }, [seriesQuery.data])

  // ── Chips actifs ──────────────────────────────────────────────────────────
  const chips: FilterChip[] = useMemo(() => {
    const list: FilterChip[] = []
    if (filters.q) {
      list.push({ key: 'q', label: `Recherche : « ${filters.q} »` })
    }
    if (filters.tags && filters.tags.length > 0) {
      const labelByid = new Map((allTagsQuery.data ?? []).map((t) => [t.id, t.label_fr]))
      for (const id of filters.tags) {
        list.push({ key: `tag-${id}`, label: `Tag : ${labelByid.get(id) ?? id}` })
      }
    }
    if (filters.series_id !== undefined) {
      const series = (seriesQuery.data ?? []).find((s) => s.id === filters.series_id)
      list.push({
        key: 'series_id',
        label: `Série : ${series?.name_fr ?? filters.series_id}`,
      })
    }
    if (filters.orientation) {
      list.push({
        key: 'orientation',
        label: `Format : ${WORK_ORIENTATION_FR[filters.orientation]}`,
      })
    }
    return list
  }, [
    filters.q,
    filters.tags,
    filters.series_id,
    filters.orientation,
    allTagsQuery.data,
    seriesQuery.data,
  ])

  const handleRemoveChip = (key: string) => {
    if (key === 'q') {
      removeFilter('q')
      return
    }
    if (key.startsWith('tag-')) {
      const id = Number.parseInt(key.slice(4), 10)
      const next = (filters.tags ?? []).filter((t) => t !== id)
      setFilter('tags', next.length > 0 ? next : undefined)
      return
    }
    if (key === 'series_id') {
      removeFilter('series_id')
      return
    }
    if (key === 'orientation') {
      removeFilter('orientation')
      return
    }
  }

  // S5c — la route `/works` retourne `WorkListResponse{items, total,
  // page, page_size, has_next}`. La page consomme `.items` pour la grille
  // courante et conserve `total`/`has_next` pour le bandeau `<Pagination>`.
  const works = worksQuery.data?.items ?? []
  const total = worksQuery.data?.total ?? 0
  const hasNext = worksQuery.data?.has_next ?? false
  const isInitialLoading = worksQuery.isLoading && works.length === 0
  const hasFilters = activeKeys.length > 0
  // Eyebrow reflète la **cardinalité totale**, pas seulement la page —
  // sinon "Bibliothèque · 50 œuvres" sur une bibliothèque de 200 induit
  // en erreur. Reste en singulier/pluriel correct via la valeur totale.
  const eyebrow = `Bibliothèque · ${total} œuvre${total > 1 ? 's' : ''}`

  // Tab actif dérivé : `has_pending_drafts` prend la priorité sur `status`
  // car les deux dimensions sont mutuellement exclusives côté UI (tabs).
  const statusTab: StatusTab = filters.has_pending_drafts
    ? 'pending_drafts'
    : (filters.status ?? 'all')
  const handleStatusTab = (next: StatusTab) => {
    if (next === 'all') {
      removeFilter('status')
      removeFilter('has_pending_drafts')
    } else if (next === 'pending_drafts') {
      removeFilter('status')
      setFilter('has_pending_drafts', true)
    } else {
      removeFilter('has_pending_drafts')
      setFilter('status', next)
    }
  }
  // Ordre proto Direction C : Toutes, Brouillons, Drafts à valider, Publiées,
  // Archivées. On insère « pending_drafts » entre `draft` et `published`.
  const statusTabItems = [
    { value: 'all' as StatusTab, label: 'Toutes' },
    { value: 'draft' as StatusTab, label: WORK_STATUS_FR.draft },
    { value: 'pending_drafts' as StatusTab, label: 'Drafts à valider' },
    { value: 'published' as StatusTab, label: WORK_STATUS_FR.published },
    { value: 'archived' as StatusTab, label: WORK_STATUS_FR.archived },
  ]

  return (
    <Page width="wide">
      <Page.Header
        eyebrow={eyebrow}
        title="Œuvres"
        actions={
          <div className="works-page-header-actions">
            <SearchInput
              value={searchDraft}
              onChange={setSearchDraft}
              onSearch={(v) => setFilter('q', v || undefined)}
              placeholder="Rechercher dans titre, description, story…"
              ariaLabel="Recherche dans la bibliothèque"
              className="works-page-header-search"
            />
            <Button
              variant="primary"
              onClick={() => navigate('/works/new')}
              aria-label="Nouvelle œuvre"
            >
              <Plus size={14} aria-hidden="true" />
              Nouvelle œuvre
            </Button>
          </div>
        }
      />

      <div className="works-page-tabs-row">
        <Tabs<StatusTab>
          value={statusTab}
          onChange={handleStatusTab}
          items={statusTabItems}
          ariaLabel="Filtrer par statut"
        />
        <div className="works-page-tabs-controls">
          <label className="works-page-control">
            <span className="works-page-control-label">Série</span>
            <Select
              value={filters.series_id !== undefined ? String(filters.series_id) : ''}
              onChange={(e) => {
                const v = e.target.value
                setFilter('series_id', v ? Number(v) : undefined)
              }}
              aria-label="Filtrer par série"
            >
              <option value="">Toutes séries</option>
              {seriesOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </label>
          <label className="works-page-control">
            <span className="works-page-control-label">Tri</span>
            <Select
              value={sort}
              onChange={(e) => setFilter('sort', e.target.value as WorkSortKey)}
              aria-label="Trier"
            >
              {WORK_SORT_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {WORK_SORT_FR[s]}
                </option>
              ))}
            </Select>
          </label>
        </div>
      </div>

      <div className="works-page-secondary-filters">
        <TagPicker
          value={selectedTags}
          onChange={(next) =>
            setFilter('tags', next.length > 0 ? next.map((t) => Number(t.id)) : undefined)
          }
          onSearch={async (q, kind) => {
            const data = await tagsApi.list({ q, kind, limit: 20 })
            return data.map(tagResponseToTag)
          }}
          placeholder="Filtrer par tag…"
          ariaLabel="Filtrer par tags"
        />
        <Select
          value={filters.orientation ?? ''}
          onChange={(e) => {
            const v = e.target.value
            setFilter('orientation', v ? (v as typeof filters.orientation) : undefined)
          }}
          aria-label="Filtrer par format"
        >
          <option value="">Tous formats</option>
          {WORK_ORIENTATION_OPTIONS.map((o) => (
            <option key={o} value={o}>
              {WORK_ORIENTATION_FR[o]}
            </option>
          ))}
        </Select>
      </div>

      {chips.length > 0 && (
        <FilterChips chips={chips} onRemove={handleRemoveChip} onClearAll={clear} />
      )}

      {isInitialLoading ? (
        <p className="ui-text-muted">Chargement…</p>
      ) : works.length === 0 ? (
        hasFilters ? (
          <EmptyState
            icon={ImagePlus}
            title="Aucun résultat"
            description="Aucune œuvre ne correspond aux filtres actifs."
            action={
              <Button variant="ghost" onClick={clear}>
                Effacer les filtres
              </Button>
            }
          />
        ) : (
          <EmptyState
            icon={ImagePlus}
            title="Aucune œuvre pour le moment"
            description="Ajoutez votre première œuvre pour démarrer la bibliothèque."
            action={
              <Button variant="primary" onClick={() => navigate('/works/new')}>
                <Plus size={14} aria-hidden="true" />
                Nouvelle œuvre
              </Button>
            }
          />
        )
      ) : (
        <>
          <ul className="works-grid" role="list">
            {works.map((work) => (
              <WorkCard key={work.id} work={work} />
            ))}
          </ul>
          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            hasNext={hasNext}
            onPageChange={(next) => setFilter('page', next === 1 ? undefined : next)}
            ariaLabel="Pagination de la bibliothèque"
          />
        </>
      )}
    </Page>
  )
}
